import React, { useEffect, useState } from "react";
import { format } from "date-fns";
import { useNavigate, useLocation } from "react-router-dom";
import api from "../../services/api";
import DebounceInput from "../../components/DebounceInput";
import { isSameMonth, mapActivitiesToOccupations } from "../../utils/occupation";

const DEFAULT_AVATAR = "https://www.gravatar.com/avatar/00000000000000000000000000000000?d=mp&f=y";

export default function ByProjectHighConfidence({ businessUnit }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [projectBudgets, setProjectBudgets] = useState({});
  const [occupations, setOccupations] = useState([]);
  const [filters, setFilters] = useState({
    search: "",
    isExpense: false
  });

  const openUserOccupation = (userId) => {
    const params = new URLSearchParams(location.search);
    params.set("user_modal_id", userId);
    params.set("user_modal_tab", "activity");
    navigate({ pathname: location.pathname, search: params.toString() });
  };

  // Only include months from this month forward (7 months including current)
  const months = [...Array(7)].map((_, i) => {
    const date = new Date();
    date.setDate(1);
    date.setMonth(date.getMonth() + i);
    return date;
  });

  useEffect(() => {
    loadData();
  }, [filters, businessUnit]);

  async function loadData() {
    const query = { ...filters };
    if (businessUnit._id !== "ALL") {
      query.business_unit_id = businessUnit._id;
    }
    const { data, ok } = await api.post("/budget/monthly-aggregated", query);
    if (!ok) return;

    const budgetsByProject = data.reduce((acc, item) => {
      acc[item._id] = {
        projectName: item.projectName,
        budgets: item.budgets,
        user_tjms_override: item.user_tjms_override || [],
      };
      return acc;
    }, {});
    setProjectBudgets(budgetsByProject);

    // Load forecasted activities scoped to these projects (single call)
    const projectIds = Object.keys(budgetsByProject);
    if (projectIds.length > 0) {
      const dateFrom = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
      const { data: activities } = await api.post("/activity/search", { projectIds, has_forecast: true, dateFrom });
      setOccupations(mapActivitiesToOccupations(activities));
    } else {
      setOccupations([]);
    }
  }

  const calculateMonthlyAmount = (budgets, month) => {
    return budgets.reduce((total, budget) => {
      // Skip invalid budgets
      if (!budget.startAt || !budget.endAt) return total;

      // Filter for high confidence budgets only (> 70% or confirmed)
      if (budget.type === "prediction" && (!budget.confidence || budget.confidence < 70)) {
        return total;
      }

      const startDate = new Date(budget.startAt);
      const endDate = new Date(budget.endAt);

      // Check if this budget should be included for this month
      const startDateCompare = new Date(startDate);
      startDateCompare.setDate(1);
      const endDateCompare = new Date(endDate);
      endDateCompare.setDate(1);

      const targetDate = new Date(month);
      targetDate.setDate(1);

      const isInBudgetPeriod =
        (targetDate.getFullYear() === startDateCompare.getFullYear() && targetDate.getMonth() === startDateCompare.getMonth()) ||
        (targetDate.getFullYear() === endDateCompare.getFullYear() && targetDate.getMonth() === endDateCompare.getMonth()) ||
        (targetDate > startDateCompare && targetDate < endDateCompare);

      if (!isInBudgetPeriod) return total;

      // Calculate total months in the budget period
      const totalMonths = Math.max(1, (endDateCompare.getFullYear() - startDateCompare.getFullYear()) * 12 + (endDateCompare.getMonth() - startDateCompare.getMonth()) + 1);

      // Simple monthly distribution
      const monthlyAmount = budget.amount / totalMonths;

      return total + monthlyAmount;
    }, 0);
  };

  const getPeopleDetails = (projectId, month) => {
    // Get all occupations for this project and month
    const projectOccs = occupations.filter(
      (o) => o.project_id === projectId && isSameMonth(new Date(o.month), month)
    );

    // Aggregate per user - handle weekly/monthly granularity
    const userMap = {};
    projectOccs.forEach((o) => {
      if (!userMap[o.user_id]) {
        userMap[o.user_id] = {
          ...o,
          monthlyPercent: 0,
          weeklyPercents: [],
        };
      }
      if (o.granularity === "weekly") {
        userMap[o.user_id].weeklyPercents.push(o.percent);
      } else {
        userMap[o.user_id].monthlyPercent += o.percent;
      }
    });

    // Convert to array with calculated percent
    return Object.values(userMap).map((user) => {
      let percent;
      if (user.weeklyPercents.length > 0) {
        // Average weekly entries
        percent = user.weeklyPercents.reduce((sum, p) => sum + p, 0) / user.weeklyPercents.length;
      } else {
        percent = user.monthlyPercent;
      }
      return { ...user, percent };
    });
  };

  const calculatePeopleSell = (people, projectId) => {
    
    return people.reduce((total, person) => {

      const project = projectBudgets[projectId];
      const userOverride = project?.user_tjms_override?.find(o => o.userId === person.user_id);
      
      const tjms = userOverride ? userOverride.tjms : 
                   person.user_tjms || 
                   person.user_tjm || 
                   person.tjms || 
                   person.tjm || 
                   0;
      
      if (!tjms || tjms === 0) return total;
      
      const daysPerMonth = (person.percent / 100) * 22;
      const monthlyAmount = tjms * daysPerMonth;
      
      
      return total + monthlyAmount;
    }, 0);
  };

  const getMonthStats = (month) => {
    let totalCA = 0;
    let totalInvest = 0;
    let projectCount = 0;
    let peopleCount = new Set();
    let billableProjectCosts = 0;

    let totalConfirmedBudget = 0;
    let totalHighProbabilityBudget = 0;

    // Use the same filtering logic as the table display
    Object.entries(projectBudgets)
      .filter(([_, { projectName }]) => projectName !== "Unavailable")
      .filter(([_, { projectName }]) => filterText === "" || projectName.toLowerCase().includes(filterText.toLowerCase()))
      .filter(([projectId, { budgets }]) => {
        // Check if project has budget activity OR people assigned
        const hasBudgetActivity = months.some((month) => calculateMonthlyAmount(budgets, month) > 0);
        const hasPeopleAssigned = months.some((month) => getPeopleDetails(projectId, month).length > 0);
        
        return hasBudgetActivity || hasPeopleAssigned;
      })
      .forEach(([projectId, { budgets }]) => {
        const monthlyBudget = calculateMonthlyAmount(budgets, month);
        if (monthlyBudget > 0) {
          projectCount++;

          // Separate CA (revenue) from Invest (expenses)
          budgets.forEach((budget) => {
            // Filter for high confidence budgets only (> 70% or confirmed)
            if (budget.type === "prediction" && (!budget.confidence || budget.confidence < 70)) {
              return;
            }

            const budgetMonthlyAmount = calculateMonthlyAmount([budget], month);
            if (budgetMonthlyAmount > 0) {
              if (budget.type === "invest") {
                totalInvest += budgetMonthlyAmount;
              } else {
                totalCA += budgetMonthlyAmount;
              }

              // Categorize budgets by confidence level
              if (budget.type === "invest") {
                // Invest budgets are separate
              } else {
                // All non-invest budgets above 70% count toward CA
                if (budget.type !== "prediction" || (budget.confidence && budget.confidence >= 70)) {
                  // Confirmed: 100% confidence or non-prediction projects
                  if (budget.type !== "prediction" || (budget.confidence && budget.confidence >= 100)) {
                    totalConfirmedBudget += budgetMonthlyAmount;
                  }
                  // High Probability: 70-99% confidence predictions
                  else if (budget.type === "prediction" && budget.confidence && budget.confidence >= 70) {
                    totalHighProbabilityBudget += budgetMonthlyAmount;
                  }
                }
              }
            }
          });

          const projectPeople = getPeopleDetails(projectId, month);
          projectPeople.forEach((person) => {
            peopleCount.add(person.user_id);
            if (person.user_tjm) {
              const monthlyRate = person.user_tjm * 22;
              billableProjectCosts += monthlyRate * (person.percent / 100);
            }
          });
        }
      });

    return {
      ca: totalCA,
      invest: totalInvest,
      total: totalCA + totalInvest,
      confirmedBudget: totalConfirmedBudget,
      highProbabilityBudget: totalHighProbabilityBudget,
      cost: billableProjectCosts,
      margin: (totalCA + totalInvest) - billableProjectCosts,
      marginPercent: (totalCA + totalInvest) ? Math.round((((totalCA + totalInvest) - billableProjectCosts) / (totalCA + totalInvest)) * 100) : 0,
      projects: projectCount,
      people: peopleCount.size,
    };
  };


  const getConfidenceColor = (budget) => {
    if (budget.type === "invest") return "bg-slate-100 border-l-2 border-l-blue-400";
    if (budget.type !== "prediction" || (budget.confidence && budget.confidence >= 100)) {
      return "bg-slate-50 border-l-2 border-l-emerald-400"; // Confirmed
    }
    return "bg-slate-50 border-l-2 border-l-slate-300"; // High Probability
  };

  const getConfidenceBadgeStyle = (budget) => {
    if (budget.type === "invest") return "bg-blue-100 text-blue-700 text-[10px]";
    if (budget.type !== "prediction" || (budget.confidence && budget.confidence >= 100)) {
      return "bg-emerald-100 text-emerald-700 text-[10px]"; // Confirmed
    }
    return "bg-slate-200 text-slate-600 text-[10px]"; // High Probability
  };

  const getAccessibleContrastColor = (budget) => {
    if (budget.status === "inactive") return "text-gray-400";
    return "text-gray-700";
  };

  // Add filtering capabilities
  const [filterText, setFilterText] = useState("");
  const [allocationFilter, setAllocationFilter] = useState("all"); // all, over-allocated, under-allocated, no-allocation
  const [sortBy, setSortBy] = useState("name"); // name, over-allocated, under-allocated

  // Calculate project allocation status across all months
  const getProjectAllocationStatus = (projectId, budgets) => {
    let totalBudget = 0;
    let totalAllocated = 0;
    let monthsWithBudget = 0;
    let monthsWithAllocation = 0;

    months.forEach((month) => {
      const monthlyBudget = calculateMonthlyAmount(budgets, month);
      const people = getPeopleDetails(projectId, month);
      const peopleSell = calculatePeopleSell(people, projectId);

      totalBudget += monthlyBudget;
      totalAllocated += peopleSell;

      if (monthlyBudget > 0) monthsWithBudget++;
      if (peopleSell > 0) monthsWithAllocation++;
    });

    const difference = totalBudget - totalAllocated;
    const isOverAllocated = totalAllocated > totalBudget && totalBudget > 0;
    const isUnderAllocated = totalAllocated < totalBudget && totalAllocated > 0;
    const hasNoAllocation = totalBudget > 0 && totalAllocated === 0;

    return {
      totalBudget,
      totalAllocated,
      difference,
      isOverAllocated,
      isUnderAllocated,
      hasNoAllocation,
      monthsWithBudget,
      monthsWithAllocation,
    };
  };

  const filteredProjects = Object.entries(projectBudgets)
    .filter(([_, { projectName }]) => projectName !== "Unavailable")
    .filter(([_, { projectName }]) => filterText === "" || projectName.toLowerCase().includes(filterText.toLowerCase()))
    .filter(([_, { status }]) => status !== "inactive")
    .filter(([projectId, { budgets }]) => {
      // Check if project has budget activity OR people assigned
      const hasBudgetActivity = months.some((month) => calculateMonthlyAmount(budgets, month) > 0);
      const hasPeopleAssigned = months.some((month) => getPeopleDetails(projectId, month).length > 0);
      
      return hasBudgetActivity || hasPeopleAssigned;
    })
    .filter(([projectId, { budgets }]) => {
      if (allocationFilter === "all") return true;
      
      const status = getProjectAllocationStatus(projectId, budgets);
      
      if (allocationFilter === "over-allocated") return status.isOverAllocated;
      if (allocationFilter === "under-allocated") return status.isUnderAllocated;
      if (allocationFilter === "no-allocation") return status.hasNoAllocation;
      
      return true;
    });

  const sortProjects = (projects) => {
    return [...projects].sort((a, b) => {
      const [projectIdA, { projectName: nameA, budgets: budgetsA }] = a;
      const [projectIdB, { projectName: nameB, budgets: budgetsB }] = b;

      if (sortBy === "name") {
        return nameA.localeCompare(nameB);
      }

      const statusA = getProjectAllocationStatus(projectIdA, budgetsA);
      const statusB = getProjectAllocationStatus(projectIdB, budgetsB);

      if (sortBy === "over-allocated") {
        // Sort by how much over-allocated (most over first)
        const overA = statusA.isOverAllocated ? statusA.totalAllocated - statusA.totalBudget : 0;
        const overB = statusB.isOverAllocated ? statusB.totalAllocated - statusB.totalBudget : 0;
        return overB - overA;
      }

      if (sortBy === "under-allocated") {
        // Sort by how much under-allocated (most under first)
        const underA = statusA.totalBudget - statusA.totalAllocated;
        const underB = statusB.totalBudget - statusB.totalAllocated;
        return underB - underA;
      }

      return 0;
    });
  };

  // Calculate summary stats for the filter badges
  const allocationStats = Object.entries(projectBudgets)
    .filter(([_, { projectName }]) => projectName !== "Unavailable")
    .filter(([_, { status }]) => status !== "inactive")
    .filter(([projectId, { budgets }]) => {
      const hasBudgetActivity = months.some((month) => calculateMonthlyAmount(budgets, month) > 0);
      const hasPeopleAssigned = months.some((month) => getPeopleDetails(projectId, month).length > 0);
      return hasBudgetActivity || hasPeopleAssigned;
    })
    .reduce(
      (acc, [projectId, { budgets }]) => {
        const status = getProjectAllocationStatus(projectId, budgets);
        if (status.isOverAllocated) acc.overAllocated++;
        else if (status.hasNoAllocation) acc.noAllocation++;
        else if (status.isUnderAllocated) acc.underAllocated++;
        else acc.balanced++;
        return acc;
      },
      { overAllocated: 0, underAllocated: 0, noAllocation: 0, balanced: 0 }
    );

  return (
    <div className="space-y-4 mr-4">
      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-3">
          <DebounceInput debounce={300} value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })} className="input" placeholder="Search" />
        </div>
        <div className="col-span-9 bg-white p-3 rounded-md shadow-sm border border-gray-200">
          <div className="flex justify-between items-center mb-2">
            <h2 className="text-sm font-medium text-gray-700">High Confidence Budgets (≥70%)</h2>
          </div>
          <div className="flex flex-wrap items-center gap-4 text-xs text-gray-500">
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 bg-slate-50 border-l-2 border-l-emerald-400 rounded-sm"></div>
              <span>Confirmed</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 bg-slate-50 border-l-2 border-l-slate-300 rounded-sm"></div>
              <span>High Prob (70-99%)</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 bg-slate-100 border-l-2 border-l-blue-400 rounded-sm"></div>
              <span>Invest</span>
            </div>
          </div>
        </div>
      </div>

      {/* Allocation Filters */}
      <div className="flex flex-wrap items-center gap-3 text-xs">
        <div className="flex gap-1 bg-gray-100 p-0.5 rounded-md">
          <button
            onClick={() => setAllocationFilter("all")}
            className={`px-2.5 py-1 rounded transition-colors ${
              allocationFilter === "all" ? "bg-white text-gray-700 shadow-sm" : "text-gray-500 hover:text-gray-700"
            }`}>
            All
          </button>
          <button
            onClick={() => setAllocationFilter("over-allocated")}
            className={`px-2.5 py-1 rounded transition-colors flex items-center gap-1 ${
              allocationFilter === "over-allocated" ? "bg-white text-red-600 shadow-sm" : "text-gray-500 hover:text-gray-700"
            }`}>
            <span className="w-1.5 h-1.5 bg-red-500 rounded-full"></span>
            Over ({allocationStats.overAllocated})
          </button>
          <button
            onClick={() => setAllocationFilter("no-allocation")}
            className={`px-2.5 py-1 rounded transition-colors flex items-center gap-1 ${
              allocationFilter === "no-allocation" ? "bg-white text-amber-600 shadow-sm" : "text-gray-500 hover:text-gray-700"
            }`}>
            <span className="w-1.5 h-1.5 bg-amber-400 rounded-full"></span>
            No Alloc ({allocationStats.noAllocation})
          </button>
          <button
            onClick={() => setAllocationFilter("under-allocated")}
            className={`px-2.5 py-1 rounded transition-colors flex items-center gap-1 ${
              allocationFilter === "under-allocated" ? "bg-white text-gray-600 shadow-sm" : "text-gray-500 hover:text-gray-700"
            }`}>
            <span className="w-1.5 h-1.5 bg-slate-300 rounded-full"></span>
            Under ({allocationStats.underAllocated})
          </button>
        </div>

        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className="text-xs border border-gray-200 rounded px-2 py-1 bg-white text-gray-600 focus:outline-none focus:ring-1 focus:ring-gray-300">
          <option value="name">Sort: Name</option>
          <option value="over-allocated">Sort: Most Over</option>
          <option value="under-allocated">Sort: Most Under</option>
        </select>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead className="sticky top-0 z-10">
            <tr className="border-b bg-gray-50 shadow-sm">
              <th className="text-left p-2 font-medium text-gray-700">Project</th>
              {months.map((month) => {
                const stats = getMonthStats(month);
                return (
                  <th key={month.toString()} className="text-left p-2 min-w-[140px]">
                    <div className="font-medium text-gray-700 text-xs">{format(month, "MMM yyyy")}</div>
                    <div className="text-[10px] text-gray-500 mt-1 space-y-0.5">
                      <div className="flex justify-between">
                        <span>Confirmed</span>
                        <span className="text-gray-600">€{Math.round(stats.confirmedBudget).toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>High Prob</span>
                        <span className="text-gray-500">€{Math.round(stats.highProbabilityBudget).toLocaleString()}</span>
                      </div>
                      {stats.invest > 0 && (
                        <div className="flex justify-between text-blue-600">
                          <span>Invest</span>
                          <span>€{Math.round(stats.invest).toLocaleString()}</span>
                        </div>
                      )}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {sortProjects(filteredProjects).map(([projectId, { projectName, budgets }]) => {
              const allocationStatus = getProjectAllocationStatus(projectId, budgets);
              
              const getStatusIndicator = () => {
                if (allocationStatus.isOverAllocated) return "bg-red-500";
                if (allocationStatus.hasNoAllocation) return "bg-amber-400";
                if (allocationStatus.isUnderAllocated) return "bg-slate-300";
                return "bg-emerald-400";
              };

              const getStatusText = () => {
                if (allocationStatus.isOverAllocated) {
                  return <span className="text-red-600">+€{Math.round(allocationStatus.totalAllocated - allocationStatus.totalBudget).toLocaleString()}</span>;
                }
                if (allocationStatus.hasNoAllocation) {
                  return <span className="text-amber-600">No allocation</span>;
                }
                if (allocationStatus.isUnderAllocated) {
                  return <span className="text-gray-500">€{Math.round(allocationStatus.totalBudget - allocationStatus.totalAllocated).toLocaleString()} avail</span>;
                }
                return <span className="text-emerald-600">Balanced</span>;
              };

              return (
                <tr key={projectId} className="border-b border-gray-100 hover:bg-gray-50/50 transition-colors">
                  <td className="p-2 align-top">
                    <div className="flex items-start gap-2">
                      <div className={`w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0 ${getStatusIndicator()}`}></div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <div
                            onClick={() => window.open(`/project/${projectId}/budget-admin`, "_blank")}
                            className="font-medium text-gray-800 hover:text-gray-600 hover:underline cursor-pointer text-sm">
                            {projectName}
                          </div>
                        </div>
                        <div className="text-[10px] text-gray-400 mt-0.5 flex gap-2">
                          <span>Budget €{Math.round(allocationStatus.totalBudget).toLocaleString()}</span>
                          <span>·</span>
                          <span>Alloc €{Math.round(allocationStatus.totalAllocated).toLocaleString()}</span>
                          <span>·</span>
                          {getStatusText()}
                        </div>
                      </div>
                    </div>
                  </td>
                  {months.map((month) => {
                    // Find the budgets that apply to this month (high confidence only)
                    const monthBudgets = budgets.filter((budget) => {
                      // Filter for high confidence budgets only
                      if (budget.type === "prediction" && (!budget.confidence || budget.confidence < 70)) {
                        return false;
                      }

                      const startDate = new Date(budget.startAt);
                      const endDate = new Date(budget.endAt);
                      const currentDate = new Date(month);
                      currentDate.setDate(1);
                      const startDateCompare = new Date(startDate);
                      startDateCompare.setDate(1);
                      const endDateCompare = new Date(endDate);
                      endDateCompare.setDate(1);

                      return (
                        (currentDate.getFullYear() === startDateCompare.getFullYear() && currentDate.getMonth() === startDateCompare.getMonth()) ||
                        (currentDate.getFullYear() === endDateCompare.getFullYear() && currentDate.getMonth() === endDateCompare.getMonth()) ||
                        (currentDate > startDateCompare && currentDate < endDateCompare)
                      );
                    });

                    const monthlyTotal = calculateMonthlyAmount(budgets, month);

                    const people = getPeopleDetails(projectId, month);
                    const peopleSell = calculatePeopleSell(people, projectId);
                    
                    // Calculate monthly allocation status
                    const isMonthOverAllocated = monthlyTotal > 0 && peopleSell > monthlyTotal;
                    const isMonthUnderAllocated = monthlyTotal > 0 && peopleSell > 0 && peopleSell < monthlyTotal;
                    const hasNoPeopleAllocated = monthlyTotal > 0 && peopleSell === 0;

                    const getCellBorderStyle = () => {
                      if (isMonthOverAllocated) return "border-red-200 bg-red-50/30";
                      if (hasNoPeopleAllocated) return "border-amber-200 bg-amber-50/30";
                      return "border-gray-100 bg-white";
                    };
                    
                    return (
                      <td key={month.toString()} className="p-1.5 align-top">
                        <div className={`px-2 py-1.5 rounded text-xs border ${getCellBorderStyle()} ${monthlyTotal === 0 && people.length === 0 ? 'opacity-40' : ''}`}>
                          {/* Budget Header */}
                          <div className="flex justify-between items-center mb-1">
                            <span className="text-[10px] text-gray-400 uppercase tracking-wide">Budget</span>
                            <span className={`font-medium ${monthlyTotal === 0 ? 'text-gray-400' : 'text-gray-700'}`}>
                              €{Math.round(monthlyTotal).toLocaleString()}
                            </span>
                          </div>

                          {/* People Allocation */}
                          {people.length > 0 && (
                            <div className="border-t border-gray-100 pt-1 mt-1">
                              <div className="flex justify-between items-center text-[10px] text-gray-400 mb-0.5">
                                <span className="uppercase tracking-wide">Allocated</span>
                                <span className={`font-medium ${isMonthOverAllocated ? 'text-red-600' : 'text-gray-600'}`}>
                                  €{Math.round(peopleSell).toLocaleString()}
                                </span>
                              </div>
                              <div className="space-y-0.5">
                                {people.map((person, personIndex) => {
                                  const project = projectBudgets[projectId];
                                  const userOverride = project?.user_tjms_override?.find(o => o.userId === person.user_id);
                                  const tjms = userOverride ? userOverride.tjms : 
                                             person.user_tjms || person.user_tjm || person.tjms || person.tjm || 0;
                                  const daysPerMonth = (person.percent / 100) * 22;
                                  const monthlyAmount = tjms * daysPerMonth;
                                  
                                  return (
                                    <div key={person._id || personIndex} className="text-[9px] text-gray-500 flex items-center justify-between gap-1">
                                      <div className="flex items-center gap-1 min-w-0">
                                        <img 
                                          src={person.user_avatar || DEFAULT_AVATAR} 
                                          alt="" 
                                          className="w-4 h-4 rounded-full object-cover flex-shrink-0"
                                        />
                                        <span 
                                          onClick={() => openUserOccupation(person.user_id)}
                                          className="truncate hover:text-gray-700 hover:underline cursor-pointer"
                                        >
                                          {person.user_name}
                                        </span>
                                        <span className="text-gray-400 flex-shrink-0">({person.percent}%)</span>
                                      </div>
                                      <span className="text-gray-600 flex-shrink-0">€{Math.round(monthlyAmount).toLocaleString()}</span>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}

                          {/* Budget Items */}
                          {monthBudgets.length > 0 && (
                            <div className="border-t border-gray-100 pt-1 mt-1 space-y-1">
                              {monthBudgets.map((budget, index) => {
                                const budgetMonthlyAmount = calculateMonthlyAmount([budget], month);
                                return (
                                  <div
                                    key={budget._id || index}
                                    className={`px-1.5 py-1 rounded ${getConfidenceColor(budget)} ${budgetMonthlyAmount === 0 ? 'opacity-50' : ''}`}
                                    title={`${budget.name}: €${Math.round(budgetMonthlyAmount).toLocaleString()}`}>
                                    <div className="flex justify-between items-center gap-1">
                                      <span className={`${getAccessibleContrastColor(budget)} truncate text-[10px]`}>{budget.name}</span>
                                      <span className={`${getConfidenceBadgeStyle(budget)} px-1 py-0.5 rounded whitespace-nowrap`}>
                                        €{Math.round(budgetMonthlyAmount).toLocaleString()}
                                      </span>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}

                          {/* Empty state */}
                          {monthlyTotal === 0 && people.length === 0 && (
                            <div className="text-[10px] text-gray-300 text-center py-1">—</div>
                          )}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

    </div>
  );
}
